import {
  CareInsightsMetrics,
  CareOverviewInsights,
  ProviderInsights,
} from '@lyrahealth-inc/shared-app-logic/src/models/careInsights/CareInsights'

import { AxiosInstance, baseApi } from '../../../data/baseApi'

type GetProviderInsightsOverviewArgs = {
  providerId: string
  currentDate: string
}

type GetCareInsightsMetricsArgs = {
  providerId: string
  startDate: string
  endDate: string
}

type GetProviderInsightsArgs = {
  providerId: string
  startDate: {
    year: number
    month: number
    day: number
  }
  endDate: {
    year: number
    month: number
    day: number
  }
}

type GetEpisodeInsightsArgs = {
  episodeId: string
  providerId: string
}

type GetProviderAccessedCareInsightsPageArgs = {
  id: string
}

type UpdateProviderAccessedCareInsightsPageArgs = {
  id: string
  data: { value: boolean; value_type: string }
}

const careInsightsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCareInsightsOverview: builder.query<CareOverviewInsights[], GetProviderInsightsOverviewArgs>({
      query: ({ providerId, currentDate }) => ({
        method: 'get',
        url: `/v1/providers/${providerId}/insights/overview`,
        params: { current_date: currentDate },
      }),
    }),
    getCareInsightsMetrics: builder.query<CareInsightsMetrics, GetCareInsightsMetricsArgs>({
      query: ({ providerId, startDate, endDate }) => ({
        method: 'get',
        url: `/v1/providers/${providerId}/insights/metrics`,
        params: { start_date: startDate, end_date: endDate },
      }),
    }),
    getProviderInsights: builder.query<ProviderInsights, GetProviderInsightsArgs>({
      query: ({ providerId, startDate, endDate }) => ({
        method: 'post',
        url: `/api/v1/session-quality/provider/retrieve`,
        data: { provider_id: { value: providerId }, start_date: startDate, end_date: endDate },
        axiosInstanceType: AxiosInstance.GENAI,
        ignore404: true,
      }),
    }),
    getEpisodeInsights: builder.query<ProviderInsights, GetEpisodeInsightsArgs>({
      query: ({ episodeId, providerId }) => ({
        method: 'post',
        url: `/api/v1/session-quality/episode/retrieve`,
        data: { episode_id: episodeId, provider_id: { value: providerId } },
        axiosInstanceType: AxiosInstance.GENAI,
        ignore404: true,
      }),
    }),
    getProviderAccessedCareInsightsPage: builder.query<boolean, GetProviderAccessedCareInsightsPageArgs>({
      query: ({ id }) => ({
        method: 'get',
        url: `/v1/providers/property/${id}/accessedCareInsightsPage`,
        ignore404: true,
      }),
    }),
    updateProviderAccessedCareInsightsPage: builder.mutation<void, UpdateProviderAccessedCareInsightsPageArgs>({
      query: ({ id, data }) => ({
        method: 'put',
        url: `/v1/providers/property/${id}/accessedCareInsightsPage`,
        data,
      }),
      onQueryStarted: async ({ id, data }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          careInsightsApi.util.updateQueryData('getProviderAccessedCareInsightsPage', { id }, () => {
            return data.value
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),
  }),
})

export const {
  useGetCareInsightsOverviewQuery,
  useGetCareInsightsMetricsQuery,
  useGetProviderInsightsQuery,
  useGetEpisodeInsightsQuery,
  useGetProviderAccessedCareInsightsPageQuery,
  useUpdateProviderAccessedCareInsightsPageMutation,
} = careInsightsApi
export default careInsightsApi
